import React, {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";
import authService, {
  ChangePasswordRequest,
  DeleteAccountRequest,
  ForgotPasswordRequest,
  LoginRequest,
  RegisterRequest,
  ResendVerificationRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  User,
} from "../services/api/authService";

interface AuthContextType {
  // Auth state
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isEmailVerified: boolean;

  // Auth actions
  login: (credentials: LoginRequest) => Promise<User>;
  register: (userData: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;

  // Profile management
  updateProfile: (userData: UpdateProfileRequest) => Promise<void>;
  changePassword: (passwordData: ChangePasswordRequest) => Promise<void>;
  getProfile: () => Promise<void>;

  // Password reset
  forgotPassword: (data: ForgotPasswordRequest) => Promise<void>;
  resetPassword: (data: ResetPasswordRequest) => Promise<void>;

  // Email verification
  verifyEmail: (token: string) => Promise<void>;
  resendVerification: (data: ResendVerificationRequest) => Promise<void>;

  // Account management
  deleteAccount: (data: DeleteAccountRequest) => Promise<void>;

  // Token management
  refreshToken: () => Promise<void>;
  checkTokenValidity: () => boolean;

  // Error handling
  error: string | null;
  clearError: () => void;

  // Success handling
  success: string | null;
  clearSuccess: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Derived state
  const isAuthenticated = !!user && authService.isAuthenticated();
  const isEmailVerified = user?.isEmailVerified || false;

  // Initialize auth state on app load
  useEffect(() => {
    initializeAuth();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto token refresh
  useEffect(() => {
    let refreshInterval: NodeJS.Timeout;

    if (isAuthenticated) {
      // Set up automatic token refresh
      refreshInterval = setInterval(() => {
        if (authService.isTokenExpired()) {
          handleTokenRefresh();
        }
      }, 5 * 60 * 1000); // Check every 5 minutes
    }

    return () => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  /**
   * Initialize authentication state from stored data
   */
  const initializeAuth = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const storedUser = authService.getStoredUser();
      const storedToken = authService.getToken();

      if (storedUser && storedToken) {
        // Check if token is still valid
        if (!authService.isTokenExpired()) {
          setUser(storedUser);

          // Optionally fetch fresh user data
          try {
            await getProfile();
          } catch (error) {
            // If profile fetch fails, keep stored user but don't error out
            console.warn("Failed to refresh user profile:", error);
          }
        } else {
          // Token expired, try to refresh
          try {
            await handleTokenRefresh();
          } catch (error) {
            // Refresh failed, clear auth data
            authService.clearAuthData();
            setUser(null);
          }
        }
      }
    } catch (error) {
      console.error("Auth initialization error:", error);
      authService.clearAuthData();
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle token refresh
   */
  const handleTokenRefresh = async () => {
    try {
      await authService.refreshToken();
      // Token is automatically updated in authService
    } catch (error) {
      console.error("Token refresh failed:", error);
      // Force logout on refresh failure
      await handleLogout();
      throw error;
    }
  };

  /**
   * Clear error state
   */
  const clearError = () => {
    setError(null);
  };

  /**
   * Clear success state
   */
  const clearSuccess = () => {
    setSuccess(null);
  };

  /**
   * Set error with auto-clear
   */
  const setErrorWithTimeout = (message: string) => {
    setError(message);
    setTimeout(() => setError(null), 5000);
  };

  /**
   * Set success with auto-clear
   */
  const setSuccessWithTimeout = (message: string) => {
    setSuccess(message);
    setTimeout(() => setSuccess(null), 5000);
  };

  /**
   * Login user
   */
  const login = async (credentials: LoginRequest): Promise<User> => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.login(credentials);
      setUser(response.data.user);
      setSuccessWithTimeout("Login successful!");
      return response.data.user;
    } catch (error: any) {
      const errorMessage =
        error.message || "Login failed. Please check your credentials.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Register new user
   */
  const register = async (userData: RegisterRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.register(userData);
      setUser(response.data.user);
      setSuccessWithTimeout(
        "Registration successful! Please check your email for verification."
      );
    } catch (error: any) {
      const errorMessage =
        error.message || "Registration failed. Please try again.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Logout user
   */
  const logout = async () => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.logout();
      setUser(null);
      setSuccessWithTimeout("Logged out successfully!");
    } catch (error: any) {
      // Clear local data even if API logout fails
      authService.clearAuthData();
      setUser(null);
      console.error("Logout error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Handle logout (internal)
   */
  const handleLogout = async () => {
    authService.clearAuthData();
    setUser(null);
  };

  /**
   * Get user profile
   */
  const getProfile = async () => {
    setError(null);

    try {
      const response = await authService.getProfile();
      setUser(response.data);
    } catch (error: any) {
      const errorMessage = error.message || "Failed to fetch profile.";
      setErrorWithTimeout(errorMessage);
      throw error;
    }
  };

  /**
   * Update user profile
   */
  const updateProfile = async (userData: UpdateProfileRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authService.updateProfile(userData);
      setUser(response.data);
      setSuccessWithTimeout("Profile updated successfully!");
    } catch (error: any) {
      const errorMessage = error.message || "Failed to update profile.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Change user password
   */
  const changePassword = async (passwordData: ChangePasswordRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.changePassword(passwordData);
      setSuccessWithTimeout("Password changed successfully!");
    } catch (error: any) {
      const errorMessage = error.message || "Failed to change password.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Request password reset
   */
  const forgotPassword = async (data: ForgotPasswordRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.forgotPassword(data);
      setSuccessWithTimeout(
        "Password reset email sent! Please check your inbox."
      );
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to send password reset email.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Reset password with token
   */
  const resetPassword = async (data: ResetPasswordRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.resetPassword(data);
      setSuccessWithTimeout(
        "Password reset successfully! You can now login with your new password."
      );
    } catch (error: any) {
      const errorMessage = error.message || "Failed to reset password.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Verify email with token
   */
  const verifyEmail = async (token: string) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.verifyEmail(token);
      // Refresh user data to get updated verification status
      await getProfile();
      setSuccessWithTimeout("Email verified successfully!");
    } catch (error: any) {
      const errorMessage = error.message || "Failed to verify email.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Resend verification email
   */
  const resendVerification = async (data: ResendVerificationRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.resendVerification(data);
      setSuccessWithTimeout(
        "Verification email sent! Please check your inbox."
      );
    } catch (error: any) {
      const errorMessage =
        error.message || "Failed to send verification email.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Delete user account
   */
  const deleteAccount = async (data: DeleteAccountRequest) => {
    setIsLoading(true);
    setError(null);

    try {
      await authService.deleteAccount(data);
      setUser(null);
      setSuccessWithTimeout("Account deleted successfully.");
    } catch (error: any) {
      const errorMessage = error.message || "Failed to delete account.";
      setErrorWithTimeout(errorMessage);
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Refresh authentication token
   */
  const refreshToken = async () => {
    try {
      await handleTokenRefresh();
    } catch (error: any) {
      const errorMessage = error.message || "Failed to refresh authentication.";
      setErrorWithTimeout(errorMessage);
      throw error;
    }
  };

  /**
   * Check if current token is valid
   */
  const checkTokenValidity = (): boolean => {
    return authService.isAuthenticated() && !authService.isTokenExpired();
  };

  const value: AuthContextType = {
    // Auth state
    user,
    isAuthenticated,
    isLoading,
    isEmailVerified,

    // Auth actions
    login,
    register,
    logout,

    // Profile management
    updateProfile,
    changePassword,
    getProfile,

    // Password reset
    forgotPassword,
    resetPassword,

    // Email verification
    verifyEmail,
    resendVerification,

    // Account management
    deleteAccount,

    // Token management
    refreshToken,
    checkTokenValidity,

    // Error handling
    error,
    clearError,

    // Success handling
    success,
    clearSuccess,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export default AuthContext;
